import { Maybe } from "@clockwise/schema";
import React, { useMemo } from "react";
// time-allocation imports
import * as s from "./TimeAllocation.styles";
import { TimeAllocationSegmentedBar } from "./TimeAllocationSegmentedBar";

const labels = {
  FOCUS_TIME: "Focus Time",
  MEETINGS: "meetings",
  OTHER: "other events",
  FRAGMENTED_TIME: "fragmented time",
};

type MaybeNumber = Maybe<number> | undefined;

type TimeAllocationProps = {
  focusMinutes: MaybeNumber;
  busyMinutes: MaybeNumber;
  unclassifiedMinutes?: MaybeNumber;
  hideJudgement?: boolean;
  fragmentedMinutes: MaybeNumber;
  totalMinutes: MaybeNumber;
};

///////////////
// Component //
///////////////

const getSegmentBarData = ({
  focusMinutes,
  busyMinutes,
  unclassifiedMinutes,
  fragmentedMinutes,
}: {
  focusMinutes: MaybeNumber;
  busyMinutes: MaybeNumber;
  fragmentedMinutes: MaybeNumber;
  unclassifiedMinutes: MaybeNumber;
}) => {
  return [
    {
      minutes: focusMinutes ?? null,
      goalMinutes: 800,
      label: labels.FOCUS_TIME,
      color: s.focusColor,
      description:
        "Uninterrupted blocks of free time longer than 2 hours and within your working hours.",
    },
    {
      minutes: fragmentedMinutes ?? null,
      goalMinutes: 360,
      label: labels.FRAGMENTED_TIME,
      color: s.fragmentedColor,
      description: "Blocks of free time shorter than 2 hours and within your working hours.",
    },
    {
      minutes: busyMinutes ?? null,
      goalMinutes: 800,
      label: labels.MEETINGS,
      color: s.busyColor,
      description:
        "Calendar events with other attendees, or that our algorithm has identified is a meeting.",
    },
    {
      minutes: unclassifiedMinutes ?? null,
      goalMinutes: 800,
      label: labels.OTHER,
      color: s.otherColor,
      description:
        "Calendar events that Clockwise has identified are not meetings e.g. personal holds.",
    },
  ];
};

export const TimeAllocation = ({
  focusMinutes,
  busyMinutes,
  unclassifiedMinutes,
  hideJudgement,
  fragmentedMinutes,
  totalMinutes,
}: TimeAllocationProps) => {
  const segmentBarData = useMemo(() => {
    return getSegmentBarData({
      focusMinutes,
      fragmentedMinutes,
      unclassifiedMinutes,
      busyMinutes,
    }).map((barSegment) => {
      return (
        <TimeAllocationSegmentedBar
          key={barSegment.label}
          minutes={barSegment.minutes}
          goalMinutes={barSegment.goalMinutes}
          label={barSegment.label}
          color={barSegment.color}
          description={barSegment.description}
          totalMinutes={totalMinutes || null}
          hideJudgement={hideJudgement}
        />
      );
    });
  }, [
    focusMinutes,
    fragmentedMinutes,
    unclassifiedMinutes,
    busyMinutes,
    hideJudgement,
    totalMinutes,
  ]);

  return <>{segmentBarData}</>;
};
